'use client';

import { useEffect, useState, memo, useMemo } from 'react';

interface SplitTextProps {
  text: string;
  delay?: number;
  staggerDelay?: number;
  className?: string;
  charClassName?: string;
}

/**
 * SplitText - Character-by-character reveal animation
 */
function SplitTextComponent({
  text,
  delay = 0,
  staggerDelay = 0.03,
  className = '',
  charClassName = '',
}: SplitTextProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const timer = setTimeout(() => setIsVisible(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [delay]);

  const chars = useMemo(() => text.split(''), [text]);

  // For reduced motion, just show the text
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className} aria-label={text}>
      {chars.map((char, i) => (
        <span
          key={i}
          className={charClassName}
          style={{
            display: 'inline-block',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: `opacity 0.4s ease-out, transform 0.4s ease-out`,
            transitionDelay: `${i * staggerDelay}s`,
            whiteSpace: char === ' ' ? 'pre' : 'normal',
          }}
          aria-hidden="true"
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  );
}

export const SplitText = memo(SplitTextComponent);
